import React, { useState } from 'react';
import { Container, Form, Button, Row, Col, Table, Alert } from 'react-bootstrap';
import { searchProductForInbound, createInboundMovimenti } from '../services/api'; // Assicurati che queste funzioni esistano in api.js

const CaricoMagazzino = () => {
    const [identifier, setIdentifier] = useState('');
    const [searchResult, setSearchResult] = useState(null);
    const [quantityToLoad, setQuantityToLoad] = useState(1);
    const [inboundItems, setInboundItems] = useState([]);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSearch = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSearchResult(null);
        try {
            const response = await searchProductForInbound({ identifier });
            setSearchResult(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || "Errore durante la ricerca del prodotto.");
        } finally {
            setLoading(false);
        }
    };

    const handleAddItem = () => {
        if (!searchResult) {
            setError("Nessun prodotto selezionato da aggiungere.");
            return;
        }
        if (quantityToLoad <= 0) {
            setError("La quantità da caricare deve essere maggiore di zero.");
            return;
        }

        const existingItemIndex = inboundItems.findIndex(item =>
            item.product_id === searchResult.id && item.product_type === searchResult.item_type
        );

        if (existingItemIndex > -1) {
            // Update quantity if item already in list
            const updatedItems = [...inboundItems];
            updatedItems[existingItemIndex].quantity += quantityToLoad;
            setInboundItems(updatedItems);
        } else {
            // Add new item
            setInboundItems(prev => [
                ...prev,
                {
                    product_id: searchResult.id,
                    product_type: searchResult.item_type,
                    quantity: quantityToLoad,
                    description: `${searchResult.titolo} (${searchResult.sku || searchResult.barcode || 'N/A'})`
                }
            ]);
        }

        setIdentifier('');
        setSearchResult(null);
        setQuantityToLoad(1);
        setSuccessMessage(null);
    };

    const handleRemoveItem = (index) => {
        setInboundItems(prev => prev.filter((_, i) => i !== index));
    };

    const handleConfirmInbound = async () => {
        if (inboundItems.length === 0) {
            setError("Nessun articolo nella lista di carico.");
            return;
        }

        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            await createInboundMovimenti({ items: inboundItems });
            setSuccessMessage("Carico magazzino completato con successo!");
            setInboundItems([]); // Clear list after successful inbound
        } catch (err) {
            setError(err.response?.data?.detail || "Errore durante la conferma del carico.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container fluid>
            <h2 className="mb-4">Carico Magazzino</h2>

            {error && <Alert variant="danger">{error}</Alert>}
            {successMessage && <Alert variant="success">{successMessage}</Alert>}

            <Form onSubmit={handleSearch} className="mb-4">
                <Row className="align-items-end">
                    <Col md={6}>
                        <Form.Group controlId="identifierSearch">
                            <Form.Label>Cerca Prodotto (Barcode, SKU, Titolo/Descrizione)</Form.Label>
                            <Form.Control
                                type="text"
                                value={identifier}
                                onChange={(e) => setIdentifier(e.target.value)}
                                placeholder="Inserisci barcode, SKU o parte del titolo/descrizione"
                                required
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? 'Ricerca...' : 'Cerca'}
                        </Button>
                    </Col>
                </Row>
            </Form>

            {searchResult && (
                <div className="mb-4 p-3 border rounded">
                    <h5>Prodotto Trovato:</h5>
                    <p><strong>ID:</strong> {searchResult.id}</p>
                    <p><strong>Tipo:</strong> {searchResult.item_type}</p>
                    <p><strong>Titolo:</strong> {searchResult.titolo}</p>
                    <p><strong>SKU:</strong> {searchResult.sku || 'N/A'}</p>
                    <p><strong>Barcode:</strong> {searchResult.barcode || 'N/A'}</p>
                    <p><strong>Prezzo:</strong> {searchResult.prezzo ? `€${searchResult.prezzo.toFixed(2)}` : 'N/A'}</p>
                    <p><strong>Quantità Attuale:</strong> {searchResult.quantita_attuale}</p>
                    <Form.Group controlId="quantityToLoad" className="mt-3">
                        <Form.Label>Quantità da Caricare</Form.Label>
                        <Form.Control
                            type="number"
                            value={quantityToLoad}
                            onChange={(e) => setQuantityToLoad(parseInt(e.target.value))}
                            min="1"
                            required
                        />
                    </Form.Group>
                    <Button variant="success" className="mt-3" onClick={handleAddItem}>
                        Aggiungi al Carico
                    </Button>
                </div>
            )}

            <h4 className="mb-3">Articoli da Caricare:</h4>
            {inboundItems.length === 0 ? (
                <Alert variant="info">Nessun articolo aggiunto per il carico.</Alert>
            ) : (
                <>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Prodotto</th>
                                <th>Tipo</th>
                                <th>Quantità</th>
                                <th>Descrizione</th>
                                <th>Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {inboundItems.map((item, index) => (
                                <tr key={index}>
                                    <td>{item.product_id}</td>
                                    <td>{item.product_type}</td>
                                    <td>{item.quantity}</td>
                                    <td>{item.description}</td>
                                    <td>
                                        <Button variant="danger" size="sm" onClick={() => handleRemoveItem(index)}>
                                            Rimuovi
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Button variant="primary" onClick={handleConfirmInbound} disabled={loading}>
                        {loading ? 'Conferma...' : 'Conferma Carico'}
                    </Button>
                </>
            )}
        </Container>
    );
};

export default CaricoMagazzino;